import React from "react"

import { Input } from "@coval/ui/input"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { SecretInput } from "../SecretInput"
import { JsonTextareaField } from "./JsonTextareaField"
import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import {
  WEBSOCKET_COMPAT_PROFILE_JSON_AUDIO,
  type WebSocketFieldProps,
} from "./types"

export const DirectModeFields: React.FC<WebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  const isJsonAudioProfile =
    metadata.websocket_compat_profile === WEBSOCKET_COMPAT_PROFILE_JSON_AUDIO

  return (
    <>
      {/* WebSocket Endpoint (Required) */}
      <div className="space-y-2">
        <FieldLabel htmlFor="ws-endpoint" tooltip={WEBSOCKET_TOOLTIPS.endpoint}>
          WebSocket Endpoint
        </FieldLabel>
        <Input
          id="ws-endpoint"
          aria-label="WebSocket Endpoint"
          value={metadata.endpoint || ""}
          placeholder="Enter your WebSocket endpoint"
          onChange={(e) => onMetadataUpdate({ endpoint: e.target.value })}
          disabled={disabled}
        />
      </div>

      {/* Initialization JSON */}
      <JsonTextareaField
        id="init-json"
        label="Initialization JSON"
        ariaLabel="Initialization JSON"
        placeholder={'{\n  "type": "session_init",\n  "config": {}\n}'}
        description={
          isJsonAudioProfile
            ? "This JSON audio preset does not use an initialization message."
            : "Optional JSON object sent to the WebSocket endpoint to initialize the connection. No message is sent if left empty."
        }
        value={metadata.initialization_json}
        fieldName="initialization_json"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        tooltip={
          isJsonAudioProfile
            ? `This JSON audio preset does not use an initialization message. ${WEBSOCKET_TOOLTIPS.initializationJson}`
            : WEBSOCKET_TOOLTIPS.initializationJson
        }
      />

      {/* Authorization Header (Optional) */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="direct-auth-header"
          tooltip={WEBSOCKET_TOOLTIPS.authorizationHeader}
        >
          Authorization Header
        </FieldLabel>
        <SecretInput
          id="direct-auth-header"
          data-testid="websocket-direct-auth-header"
          aria-label="Authorization Header"
          revealLabel="authorization header"
          value={metadata.authorization_header || ""}
          placeholder="Enter an authorization header"
          onChange={(e) =>
            onMetadataUpdate({ authorization_header: e.target.value })
          }
          disabled={disabled}
        />
      </div>

      {/* Custom Headers (Optional) */}
      <JsonTextareaField
        id="direct-custom-headers"
        label="Custom Headers"
        placeholder={'{\n  "X-Coval-Simulation-Id": "{{simulation_id}}"\n}'}
        description="Additional HTTP headers as a JSON object. Sent during the WebSocket handshake."
        value={metadata.custom_headers}
        fieldName="custom_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={3}
        tooltip={WEBSOCKET_TOOLTIPS.customHeaders}
      />
    </>
  )
}
